import { buildResult, safeParse } from '../helpers';

const storage = typeof window !== 'undefined' ? window.localStorage : null;

const toKey = (namespace, collection) => `${namespace}:${collection}`;

const readCollection = (namespace, collection) => {
  if (!storage) {
    return [];
  }

  const value = storage.getItem(toKey(namespace, collection));
  const parsed = safeParse(value, []);

  return Array.isArray(parsed) ? parsed : [];
};

const writeCollection = (namespace, collection, items) => {
  if (!storage) {
    return;
  }

  storage.setItem(toKey(namespace, collection), JSON.stringify(items));
};

export const createLocalStorageBackend = () => ({
  async list(namespace, collection) {
    try {
      return buildResult({ ok: true, data: readCollection(namespace, collection) });
    } catch (error) {
      return buildResult({ ok: false, error });
    }
  },

  async getById(namespace, collection, id) {
    try {
      const items = readCollection(namespace, collection);
      const item = items.find(entry => entry && entry.id === id) || null;
      return buildResult({ ok: true, data: item });
    } catch (error) {
      return buildResult({ ok: false, error });
    }
  },

  async create(namespace, collection, payload) {
    try {
      const items = readCollection(namespace, collection);
      items.push(payload);
      writeCollection(namespace, collection, items);
      return buildResult({ ok: true, data: payload });
    } catch (error) {
      return buildResult({ ok: false, error });
    }
  },

  async update(namespace, collection, id, payload) {
    try {
      const items = readCollection(namespace, collection);
      const index = items.findIndex(entry => entry && entry.id === id);

      if (index === -1) {
        return buildResult({ ok: false, error: new Error('Item not found') });
      }

      items[index] = payload;
      writeCollection(namespace, collection, items);
      return buildResult({ ok: true, data: payload });
    } catch (error) {
      return buildResult({ ok: false, error });
    }
  },

  async remove(namespace, collection, id) {
    try {
      const items = readCollection(namespace, collection);
      const nextItems = items.filter(entry => !(entry && entry.id === id));
      writeCollection(namespace, collection, nextItems);
      return buildResult({ ok: true, data: { removedId: id } });
    } catch (error) {
      return buildResult({ ok: false, error });
    }
  },

  async clear(namespace, collection) {
    try {
      if (!storage) {
        return buildResult({ ok: true, data: true });
      }

      if (collection) {
        storage.removeItem(toKey(namespace, collection));
      } else {
        const prefix = `${namespace}:`;
        const keysToRemove = [];

        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key && key.indexOf(prefix) === 0) {
            keysToRemove.push(key);
          }
        }

        keysToRemove.forEach(key => storage.removeItem(key));
      }

      return buildResult({ ok: true, data: true });
    } catch (error) {
      return buildResult({ ok: false, error });
    }
  }
});
